import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button2';
import { Input } from '../../../components/ui/Input';
import { Field, FieldGroup, FieldLabel } from '../../../components/ui/Field';
import { TypographyH1, TypographyMuted } from '../../../components/ui/Typography';
import { Icon } from '../../../components/ui/Icon';
import { notify } from '../../../services/notificationBus';
import { PageLayout } from '../../../components/layout';
import styles from './DocumentAssessment.module.css';

/**
 * Create an assessment from the customer's own documents.
 *
 * Deliberately asks for nothing but a name and files — no template, no column
 * mapping, no format questions. Any format the platform already converts is
 * accepted; making the customer configure anything before they see value is
 * the failure mode this whole module is designed to avoid.
 */
const ACCEPT = '.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.csv,.txt,.md,.json,.xml,.html,.png,.jpg,.jpeg';

const NewAssessment = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (incoming.length) setFiles((prev) => [...prev, ...incoming]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || files.length === 0) return;

    setSubmitting(true);
    try {
      const api = await import('../api');
      // Upload converts to markdown in the same call, which is what makes the
      // original format irrelevant from here on. A file that cannot be
      // converted still uploads and is surfaced later as unreadable coverage —
      // never as a missing work product.
      const uploaded = [];
      const rejected = [];
      for (const file of files) {
        try {
          const result = await api.uploadAssessmentDocument(file);
          const fileId = result?.file_id;
          if (fileId) uploaded.push(fileId);
          else rejected.push(file.name);
        } catch (err) {
          rejected.push(`${file.name} (${err?.message || 'upload failed'})`);
        }
      }

      if (rejected.length) {
        notify({
          type: 'warning',
          title: 'Some documents could not be uploaded',
          message: rejected.join(', '),
        });
      }

      if (!uploaded.length) throw new Error('None of the selected files could be uploaded');

      // Returns immediately with status RUNNING — the detail page polls for the
      // result, so the user is never staring at a frozen submit button.
      const assessment = await api.createAssessment(name.trim(), uploaded);
      navigate(`/athena/document-assessment/${assessment.assessment_id}`);
    } catch (err) {
      notify({
        type: 'error',
        title: 'Could not start assessment',
        message: err?.message || 'Unknown error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout
      activeNavItem="document-assessment"
      currentPage="New assessment"
      breadcrumbItems={[{ label: 'Document Assessment', path: '/athena/document-assessment' }, { label: 'New' }]}
      contentClassName={styles.contentAreaFullWidth}
    >
    <div className={styles.pageForm}>
      <header className={styles.pageHeader}>
        <div className={styles.headerText}>
          <TypographyH1>New assessment</TypographyH1>
          <TypographyMuted className={styles.subtitle}>
            Upload the TARA documents you already have, in whatever format they
            are in. Nothing needs to match a template.
          </TypographyMuted>
        </div>
      </header>

      <form className={styles.form} onSubmit={submit}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="assessment-name">Assessment name</FieldLabel>
            <Input
              id="assessment-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. BCM TARA v3"
              required
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="assessment-files">Documents</FieldLabel>
            <div
              className={styles.dropzone}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
            >
              <Icon name="upload" size="lg" tone="muted" />
              <p>Drag documents here, or choose files</p>
              <Input
                id="assessment-files"
                type="file"
                multiple
                accept={ACCEPT}
                onChange={(e) => addFiles(e.target.files)}
                aria-label="Documents to assess"
              />
            </div>
          </Field>
        </FieldGroup>

        {files.length > 0 && (
          <ul className={styles.fileList}>
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className={styles.fileRow}>
                <span>{f.name}</span>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  aria-label={`Remove ${f.name}`}
                >
                  <Icon name="close" size="xs" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.formActions}>
          <Button
            variant="outline"
            onClick={() => navigate('/athena/document-assessment')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={submitting}
            disabled={!name.trim() || files.length === 0}
          >
            {submitting ? 'Uploading…' : 'Assess documents'}
          </Button>
        </div>
      </form>
    </div>
    </PageLayout>
  );
};

export default NewAssessment;
